import { useState } from 'react';
import fileDownload from "js-file-download";
import {
    X, Download, Settings2, FileSpreadsheet, FileText,
    Plus, Trash2, Code2, Braces, ArrowUp, ArrowDown,
    GripVertical, Info, Loader2, CheckSquare, Square,
    ChevronDown, Layers, Tag, AlertCircle, ListOrdered
} from 'lucide-react';
import './ExportDownloadForm.scss';

export default function ExportDownloadForm({open, closePopup, defaultOptions = [], splitLabels = {}, onExport, title = 'Configure Export', subtitle = 'Select fields, order, and format', }) {

    const DEFAULT_SELECTED = defaultOptions.slice(0, 10).map((o, i) => ({ ...o, order: i }));
    const [splitBy, setSplitBy] = useState('');
    const [exportToExcel, setExportToExcel] = useState(true);
    const [isExporting, setIsExporting] = useState(false);
    const [isCheckedSelectAll, setIsCheckedSelectAll] = useState(false);
    const [showExportOrder, setShowExportOrder] = useState(false);
    const [downloadOptions, setDownloadOptions] = useState(defaultOptions);
    const [selectedOptions, setSelectedOptions] = useState(DEFAULT_SELECTED);
    const [variableMenuFor, setVariableMenuFor] = useState(null);
    const [splitOpen, setSplitOpen] = useState(false);
    const [dragIndex, setDragIndex] = useState(null);

    if (!open) return null;

    const standardFields = downloadOptions.filter(o => !o.customField);
    const customFields = downloadOptions.filter(o => o.customField);

    const isSelected = (option) =>
        selectedOptions.some(s => s.id === option.id);

    const toggleExportOrder = () =>
        setShowExportOrder(!showExportOrder);

    const toggleFunction = (id) => {
        const update = arr =>
            arr.map(o => {
                if (o.id !== id) return o;
                const display = o.display || '';
                return {
                    ...o,
                    display: display.startsWith('=(') && display.endsWith(')') ? display.slice(2, -1) : `=(${display})`
                };
            });
        setDownloadOptions(update);
        setSelectedOptions(update);
    };

    const insertVariable = (fieldId, varName) => {
        const update = arr => arr.map(o => o.id === fieldId ? { ...o, display: (o.display || '') + `{${varName}}` } : o);
        setDownloadOptions(update);
        setSelectedOptions(update);
        setVariableMenuFor(null);
    };

    const handleSelectAll = () => {
        if (!isCheckedSelectAll) {
            setSelectedOptions(
                downloadOptions.map((o, i) => ({ ...o, order: i }))
            );
        } else {
            setSelectedOptions([]);
        }

        setIsCheckedSelectAll(!isCheckedSelectAll);
    };

    const handleFieldSelect = (option) => {
        if (isSelected(option)) {
            setSelectedOptions(prev =>
                prev.filter(s => s.id !== option.id)
            );

            setIsCheckedSelectAll(false);
        } else {
            const updated = [
                ...selectedOptions,
                { ...option, order: selectedOptions.length }
            ];

            setSelectedOptions(updated);

            if (updated.length === downloadOptions.length) {
                setIsCheckedSelectAll(true);
            }
        }
    };

    const handleDisplayChange = (id, value) => {
        const update = arr =>
            arr.map(o =>
                o.id === id ? { ...o, display: value } : o
            );

        setDownloadOptions(update);
        setSelectedOptions(update);
    };

    const handleFieldChange = (id, value) => {
        const update = arr =>
            arr.map(o =>
                o.id === id ? { ...o, field: value } : o
            );

        setDownloadOptions(update);
        setSelectedOptions(update);
    };

    const addCustomField = () => {
        const newId = Date.now();

        const newField = {
            id: newId,
            field: `custom_field_${customFields.length + 1}`,
            description: 'Custom field',
            display: '',
            customField: true
        };

        setDownloadOptions(prev => [...prev, newField]);
        handleFieldSelect(newField);
    };

    const deleteCustomField = (id) => {
        setDownloadOptions(prev =>
            prev.filter(o => o.id !== id)
        );

        setSelectedOptions(prev =>
            prev.filter(o => o.id !== id)
        );
    };

    const moveField = (index, dir) => {
        const arr = [...selectedOptions];
        const to = dir === 'up' ? index - 1 : index + 1;
        if (to < 0 || to >= arr.length) return;
        [arr[index], arr[to]] = [arr[to], arr[index]];
        const updated = arr.map((item, i) => ({
            ...item,
            order: i,
        }));
        setSelectedOptions(updated);
    };

    const handleDragStart = (index) => {
        setDragIndex(index);
    };

    const handleDragOver = (e) => {
        e.preventDefault();
    };

    const handleDrop = (index) => {
        if (dragIndex === null || dragIndex === index) return;
        const arr = [...selectedOptions];
        const [movedItem] = arr.splice(dragIndex, 1);
        arr.splice(index, 0, movedItem);
        const updated = arr.map((item, i) => ({
            ...item,
            order: i,
        }));
        setSelectedOptions(updated);
        setDragIndex(null);
    };

    const dataURLtoFile = (dataurl) => {
        var arr = dataurl.split(',');
        var mime = arr[0].match(/:(.*?);/)[1];
        var bstr = atob(arr[1]);
        var n = bstr.length;
        var u8arr = new Uint8Array(n);
        while (n--) {
            u8arr[n] = bstr.charCodeAt(n);
        }
        return new File([u8arr], { type: mime });
    };

    const handleSubmit = async () => {
        if (selectedOptions.length === 0 || isExporting) return;
        setIsExporting(true);
        try {
            const response = await onExport({
                selectedOptions,
                splitBy,
                exportType: exportToExcel ? 'excel' : 'csv'
            });
            if (response?.status < 400) {
                const parsed = JSON.parse(response.body);
                fileDownload(dataURLtoFile(parsed.file), parsed.fileName);
                closePopup?.();
            }
        } catch (err) {
            console.error(err);
        }
        setIsExporting(false);
    };

    const customFieldSection = ()=> {
        return (
            <section className="exportDownloadForm-section">
                <div className="exportDownloadForm-section__head">
                    <span>Custom Fields</span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span className="exportDownloadForm-count">{customFields.length}</span>
                        <button className="exportDownloadForm-add-btn" onClick={addCustomField}>
                            <Plus size={13} /> Add Field
                        </button>
                    </div>
                </div>

                {customFields.length === 0 ? (
                    <div className="exportDownloadForm-empty">
                        <Info size={16} />
                        <span>No custom fields. Click "Add Field" to create one.</span>
                    </div>
                ) : (
                    <div className="exportDownloadForm-section__list">
                        {customFields.map(option => {
                            const isFunc = option.display?.startsWith('=');
                            const availVars = customFields.filter(c => c.id !== option.id);
                            return (
                                <div key={option.id} className={`exportDownloadForm-field-row custom ${isSelected(option) ? 'checked' : ''}`}>
                                    <div className="exportDownloadForm-field-row__check" onClick={() => handleFieldSelect(option)}>
                                        {isSelected(option) ? <CheckSquare size={15} /> : <Square size={15} />}
                                    </div>
                                    <div className="exportDownloadForm-field-row__body exportDownloadForm-field-row__body--custom">
                                        <div className="exportDownloadForm-custom-row">
                                            <button
                                                className={`exportDownloadForm-fn-btn ${isFunc ? 'active' : ''}`}
                                                onClick={() => toggleFunction(option.id)}
                                                title="Formula mode"
                                            >
                                                <Code2 size={13} />
                                            </button>
                                            <input
                                                className="exportDownloadForm-input exportDownloadForm-input--sm"
                                                value={option.field}
                                                onChange={e => handleFieldChange(option.id, e.target.value)}
                                                placeholder="field_name"
                                            />
                                        </div>
                                        <div className="exportDownloadForm-custom-row">
                                            <input
                                                className="exportDownloadForm-input"
                                                value={option.display}
                                                onChange={e => handleDisplayChange(option.id, e.target.value)}
                                                placeholder={isFunc ? '=FORMULA or {var}' : 'Display value or {variable}'}
                                            />
                                            <div className="exportDownloadForm-var-wrap">
                                                <button
                                                    className="exportDownloadForm-var-btn"
                                                    onClick={() => setVariableMenuFor(variableMenuFor === option.id ? null : option.id)}
                                                    title="Insert variable"
                                                >
                                                    <Braces size={13} />
                                                </button>
                                                {variableMenuFor === option.id && (
                                                    <div className="exportDownloadForm-var-menu">
                                                        {availVars.length === 0 ? (
                                                            <div className="exportDownloadForm-var-menu__empty">No other custom fields</div>
                                                        ) : availVars.map(v => (
                                                            <button key={v.id} className="exportDownloadForm-var-menu__item"
                                                                onClick={() => insertVariable(option.id, v.field)}>
                                                                <Braces size={11} /> {`{${v.field}}`}
                                                                <span>{v.display || v.field}</span>
                                                            </button>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    <button className="exportDownloadForm-del-btn" onClick={() => deleteCustomField(option.id)}>
                                        <Trash2 size={13} />
                                    </button>
                                </div>
                            );
                        })}
                    </div>
                )}
            </section>
        )
    }

    return (
        <div className="exportDownloadForm-overlay" onClick={e => e.target === e.currentTarget && closePopup?.()}>
            <div className="exportDownloadForm-modal">
                <header className="exportDownloadForm-header">
                    <div className="exportDownloadForm-header__left">
                        <div className="exportDownloadForm-header__icon">
                            <Download size={18} />
                        </div>
                        <div>
                            <h2 className="exportDownloadForm-header__title">Configure Export</h2>
                            <p className="exportDownloadForm-header__sub">Select fields, order, and format</p>
                        </div>
                    </div>
                    <div className="exportDownloadForm-header__right">
                        {isExporting && <Loader2 size={18} className="exportDownloadForm-spin" />}
                        <button className="exportDownloadForm-icon-btn" onClick={closePopup}><X size={16} /></button>
                    </div>
                </header>

                <div className="exportDownloadForm-config">
                    <div className="exportDownloadForm-config__group">
                        <span className="exportDownloadForm-config__label"><Settings2 size={13} /> Export Format</span>
                        <div className="exportDownloadForm-format-toggle">
                            <button
                                className={`exportDownloadForm-format-btn ${exportToExcel ? 'active' : ''}`}
                                onClick={() => setExportToExcel(true)}>
                                <FileSpreadsheet size={14} /> Excel (.xlsx)
                            </button>
                            <button
                                className={`exportDownloadForm-format-btn ${!exportToExcel ? 'active' : ''}`}
                                onClick={() => setExportToExcel(false)}>
                                <FileText size={14} /> CSV (.csv)
                            </button>
                        </div>
                    </div>
                    <div className="exportDownloadForm-config__group">
                        <span className="exportDownloadForm-config__label"><ListOrdered size={13} /> Export Orders</span>
                        <div className="exportDownloadForm-format-toggle">
                            <button
                                className={`exportDownloadForm-format-btn ${!showExportOrder ? 'active' : ''}`}
                                onClick={toggleExportOrder}>
                                <FileSpreadsheet size={14} /> Default Order
                            </button>
                            <button
                                className={`exportDownloadForm-format-btn ${showExportOrder ? 'active' : ''}`}
                                onClick={toggleExportOrder}>
                                <FileSpreadsheet size={14} /> Customize Order
                            </button>
                        </div>
                    </div>
                    <div className="exportDownloadForm-config__group">
                        <span className="exportDownloadForm-config__label"><Layers size={13} /> Split Sheets By</span>
                        <div className="exportDownloadForm-select-wrap">
                            <button className="exportDownloadForm-select" onClick={() => setSplitOpen(v => !v)}>
                                <span>{splitLabels[splitBy]}</span>
                                <ChevronDown size={14} className={splitOpen ? 'rotated' : ''} />
                            </button>
                            {splitOpen && (
                                <div className="exportDownloadForm-dropdown">
                                    {Object.entries(splitLabels).map(([val, label]) => (
                                        <button key={val} className={`exportDownloadForm-dropdown__item ${splitBy === val ? 'active' : ''}`}
                                            onClick={() => { setSplitBy(val); setSplitOpen(false); }}>
                                            {label}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
                <div className="exportDownloadForm-body">
                    <div className="exportDownloadForm-panel exportDownloadForm-panel--left">
                        <div className="exportDownloadForm-panel__head">
                            <span className="exportDownloadForm-panel__title">Available Fields</span>
                            <div className="exportDownloadForm-panel__controls">
                                <button className="exportDownloadForm-check-all" onClick={handleSelectAll}>
                                    {isCheckedSelectAll ? <CheckSquare size={15} /> : <Square size={15} />}
                                    <span>Select All</span>
                                </button>
                                <span className="exportDownloadForm-badge">{selectedOptions.length} selected</span>
                            </div>
                        </div>
                        <div className="exportDownloadForm-fields-scroll">
                            <section className="exportDownloadForm-section">
                                <div className="exportDownloadForm-section__head">
                                    <span>Standard Fields</span>
                                    <span className="exportDownloadForm-count">{standardFields.length}</span>
                                </div>
                                <div className="exportDownloadForm-section__list">
                                    {standardFields.map(option => (
                                        <label key={option.id} className={`exportDownloadForm-field-row ${isSelected(option) ? 'checked' : ''}`}>
                                            <div className="exportDownloadForm-field-row__check" onClick={() => handleFieldSelect(option)}>
                                                {isSelected(option) ? <CheckSquare size={15} /> : <Square size={15} />}
                                            </div>
                                            <div className="exportDownloadForm-field-row__body">
                                                <input
                                                    className="exportDownloadForm-input"
                                                    value={option.display}
                                                    onChange={e => handleDisplayChange(option.id, e.target.value)}
                                                    placeholder="Column header"
                                                />
                                                <span className="exportDownloadForm-field-row__desc">{option.description}</span>
                                            </div>
                                        </label>
                                    ))}
                                </div>
                            </section>
                            {showExportOrder && customFieldSection()}
                        </div>
                    </div>
                    <div className="exportDownloadForm-panel exportDownloadForm-panel--right">
                        {showExportOrder ?
                            <>
                                <div className="exportDownloadForm-panel__head">
                                    <span className="exportDownloadForm-panel__title">Export Order</span>
                                    <span className="exportDownloadForm-badge">{selectedOptions.length} fields</span>
                                </div>

                                {selectedOptions.length === 0 ? (
                                    <div className="exportDownloadForm-empty exportDownloadForm-empty--center">
                                        <AlertCircle size={24} />
                                        <span>Select fields from the left panel</span>
                                    </div>
                                ) : (
                                    <div className="exportDownloadForm-order-list">
                                        {selectedOptions.map((option, i) => (
                                            <div key={`${option.id}-${i}`}
                                                className={`exportDownloadForm-order-item ${option.customField ? 'custom' : ''}`}
                                                draggable
                                                onDragStart={() => handleDragStart(i)}
                                                onDragOver={handleDragOver}
                                                onDrop={() => handleDrop(i)}>
                                                    <GripVertical size={14} className="exportDownloadForm-order-item__drag" />
                                                    <div className="exportDownloadForm-order-item__num">{i + 1}</div>
                                                    <div className="exportDownloadForm-order-item__info">
                                                        <span className="exportDownloadForm-order-item__name">{option.display || option.field}</span>
                                                        <span className="exportDownloadForm-order-item__desc">{option.description}</span>
                                                    </div>
                                                    <div className={`exportDownloadForm-order-item__badge ${option.customField ? 'custom' : ''}`}>
                                                        {option.customField ? <><Tag size={10} /> Custom</> : 'Standard'}
                                                    </div>
                                                    <div className="exportDownloadForm-order-item__arrows">
                                                        <button disabled={i === 0} onClick={() => moveField(i, 'up')}><ArrowUp size={12} /></button>
                                                        <button disabled={i === selectedOptions.length - 1} onClick={() => moveField(i, 'down')}><ArrowDown size={12} /></button>
                                                    </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </>
                        :
                            <section className="exportDownloadForm-section" style={{padding: '12px'}}>
                                {customFieldSection()}
                            </section>
                        }
                    </div>
                </div>
                <footer className="exportDownloadForm-footer">
                    {selectedOptions.length === 0 && (
                        <span className="exportDownloadForm-footer__warn"><AlertCircle size={13} /> Select at least one field</span>
                    )}
                    <div className="exportDownloadForm-footer__actions">
                        <button className="exportDownloadForm-btn exportDownloadForm-btn--ghost" onClick={closePopup} disabled={isExporting}>
                            Cancel
                        </button>
                        <button
                            className="exportDownloadForm-btn exportDownloadForm-btn--primary"
                            onClick={handleSubmit}
                            disabled={isExporting || selectedOptions.length === 0}
                        >
                            {isExporting
                                ? <><Loader2 size={15} className="exportDownloadForm-spin" /> Processing…</>
                                : <><Download size={15} /> Download {exportToExcel ? 'Excel' : 'CSV'}</>}
                        </button>
                    </div>
                </footer>
            </div>
        </div>
    );
}